// src/pages/ProgressReport.jsx
// Piano With Pan — Parent Progress Report
// ─────────────────────────────────────────────────────────────────────────
// Fix: refreshTick useMemo dependency warnings.
// The correct pattern for a "force re-read from localStorage" is to use a
// plain state counter as a key on child components OR call the functions
// directly inside the render — NOT inside useMemo with the counter as a dep.
// Solution: wrap the three read calls in useCallback-free helper that reads
// fresh on every render when refreshTick changes, using useMemo with the
// counter inside the factory, avoiding the exhaustive-deps lint trigger.
// Simplest correct fix: just call the functions directly (no useMemo needed
// — they're pure reads from localStorage, not expensive computations).
// ─────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState, useCallback, useReducer } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import {
  getSummary,
  getStreakStatus,
  todayMinutes,
  DAILY_GOAL_MINUTES,
  clearProgressData,
} from "../utils/progressTracker";

function fmtDate(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleDateString(undefined, {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch {
    return "—";
  }
}

function fmtDateTime(ts) {
  if (!ts) return "—";
  try {
    const d = new Date(ts);
    return d.toLocaleDateString(undefined, { month: "short", day: "numeric" })
      + " · " + d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
  } catch {
    return "—";
  }
}

function sourceBadge(source) {
  if (source === "lyn")      return { label: "Learn Notes", color: "#10B981", bg: "#D1FAE5" };
  if (source === "practice") return { label: "Practice",    color: "#0284C7", bg: "#DBEAFE" };
  return { label: "Song", color: "#7C3AED", bg: "#EDE9FE" };
}

function StarRow({ count }) {
  return (
    <span style={{ display: "inline-flex", gap: 2, alignItems: "center" }}>
      {[1,2,3].map((i) => (
        <span key={i} style={{ fontSize: "0.85rem", filter: i<=count?"none":"grayscale(1) opacity(0.3)" }}>⭐</span>
      ))}
    </span>
  );
}

export default function ProgressReport() {
  const navigate = useNavigate();
  const [parentEmail, setParentEmail] = useState(null);
  const [confirmClear, setConfirmClear] = useState(false);

  // ── Fix: use useReducer for forceUpdate instead of a state counter
  //    passed into useMemo deps (which triggers exhaustive-deps warning).
  //    We just re-render and call the read functions directly.
  const [, forceUpdate] = useReducer((n) => n + 1, 0);

  // Load auth email (Supabase)
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (alive) setParentEmail(data?.session?.user?.email || null);
      } catch {
        if (alive) setParentEmail(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  // ── Fix: call directly on each render — localStorage reads are fast
  //    and don't need memoisation. This eliminates all three useMemo
  //    exhaustive-deps warnings with zero behaviour change.
  const summary = getSummary();
  const streak  = getStreakStatus();
  const mins    = todayMinutes();

  const handleClear = useCallback(() => {
    clearProgressData();
    setConfirmClear(false);
    forceUpdate(); // triggers re-render so the direct calls above re-read fresh data
  }, [forceUpdate]);

  const hasData = summary.totalSessions > 0;

  // Max minutes for chart y-scale
  const chartMax = (() => {
    const m = Math.max(DAILY_GOAL_MINUTES, ...summary.dailyMinutes.map(d => d.minutes), 1);
    return Math.ceil(m / 5) * 5;
  })();

  return (
    <div className="pr-root">
      <style>{CSS}</style>

      {/* Header */}
      <header className="pr-header">
        <button className="pr-back" onClick={() => navigate("/game-menu")} aria-label="Back">←</button>
        <div className="pr-title-wrap">
          <div className="pr-title">Progress Report</div>
          {parentEmail && (
            <div className="pr-subtitle">For {parentEmail}</div>
          )}
        </div>
        <button
          className="pr-print"
          onClick={() => window.print()}
          title="Print / Save as PDF"
          aria-label="Print"
        >🖨️</button>
      </header>

      <main className="pr-main">
        {!hasData && (
          <div className="pr-empty">
            <div className="pr-empty-emoji">🎹</div>
            <div className="pr-empty-title">No progress yet</div>
            <div className="pr-empty-sub">
              Once your child completes their first lesson or song, their progress will show up here.
            </div>
            <button className="pr-empty-btn" onClick={() => navigate("/game-menu")}>
              Start playing →
            </button>
          </div>
        )}

        {hasData && (
          <>
            {/* ── Top stat row ─────────────────────────────────────── */}
            <section className="pr-stats">
              <div className="pr-stat">
                <div className="pr-stat-emoji">🔥</div>
                <div className="pr-stat-val">{streak.count}</div>
                <div className="pr-stat-lbl">Day streak</div>
              </div>
              <div className="pr-stat">
                <div className="pr-stat-emoji">⏱️</div>
                <div className="pr-stat-val">{summary.totalMinutes}</div>
                <div className="pr-stat-lbl">Total minutes</div>
              </div>
              <div className="pr-stat">
                <div className="pr-stat-emoji">🎵</div>
                <div className="pr-stat-val">{summary.totalSessions}</div>
                <div className="pr-stat-lbl">Sessions</div>
              </div>
              <div className="pr-stat">
                <div className="pr-stat-emoji">🎯</div>
                <div className="pr-stat-val">{summary.avgAccuracy != null ? `${summary.avgAccuracy}%` : "—"}</div>
                <div className="pr-stat-lbl">Accuracy</div>
              </div>
              <div className="pr-stat">
                <div className="pr-stat-emoji">⭐</div>
                <div className="pr-stat-val">{summary.starsTotal}</div>
                <div className="pr-stat-lbl">Stars earned</div>
              </div>
              <div className="pr-stat">
                <div className="pr-stat-emoji">📅</div>
                <div className="pr-stat-val">{summary.uniqueDays}</div>
                <div className="pr-stat-lbl">Days played</div>
              </div>
            </section>

            {/* ── Today + daily goal ───────────────────────────────── */}
            <section className="pr-section">
              <h2 className="pr-h2">Today</h2>
              <div className="pr-today-card">
                <div className="pr-today-row">
                  <div>
                    <div className="pr-today-mins">{mins} min</div>
                    <div className="pr-today-sub">of {DAILY_GOAL_MINUTES} min daily goal</div>
                  </div>
                  <div className="pr-today-status">
                    {mins >= DAILY_GOAL_MINUTES ? "🎉 Goal reached!" : streak.playedToday ? "Keep going!" : "Not played yet today"}
                  </div>
                </div>
                <div className="pr-today-bar">
                  <div
                    className="pr-today-fill"
                    style={{
                      width: `${Math.min(100, (mins/DAILY_GOAL_MINUTES)*100)}%`,
                      background: mins >= DAILY_GOAL_MINUTES
                        ? "linear-gradient(90deg,#10B981,#059669)"
                        : "linear-gradient(90deg,#60A5FA,#3B82F6)",
                    }}
                  />
                </div>
              </div>
            </section>

            {/* ── Daily activity chart ─────────────────────────────── */}
            <section className="pr-section">
              <h2 className="pr-h2">Last 14 days</h2>
              <div className="pr-chart">
                {summary.dailyMinutes.map((d) => {
                  const pct = chartMax > 0 ? (d.minutes / chartMax) * 100 : 0;
                  const met = d.minutes >= DAILY_GOAL_MINUTES;
                  return (
                    <div key={d.date} className="pr-bar-wrap" title={`${d.date}: ${d.minutes} min`}>
                      <div className="pr-bar-value">{d.minutes || ""}</div>
                      <div className="pr-bar-track">
                        <div
                          className="pr-bar-fill"
                          style={{
                            height: `${pct}%`,
                            background: met
                              ? "linear-gradient(180deg,#34D399,#10B981)"
                              : d.minutes > 0
                              ? "linear-gradient(180deg,#93C5FD,#3B82F6)"
                              : "transparent",
                          }}
                        />
                      </div>
                      <div className="pr-bar-label">{d.label}</div>
                    </div>
                  );
                })}
              </div>
              <div className="pr-chart-legend">
                <span className="pr-legend-dot" style={{ background:"#10B981" }} /> Met daily goal
                <span className="pr-legend-dot" style={{ background:"#3B82F6", marginLeft: 14 }} /> Some play
              </div>
            </section>

            {/* ── Songs / Levels progress ─────────────────────────── */}
            <section className="pr-section">
              <h2 className="pr-h2">Songs &amp; Lessons</h2>
              {summary.perSong.length === 0 ? (
                <div className="pr-empty-small">No songs played yet.</div>
              ) : (
                <div className="pr-song-list">
                  {summary.perSong.map((s) => {
                    const badge = sourceBadge(s.source);
                    return (
                      <div key={s.key} className="pr-song-row">
                        <div className="pr-song-main">
                          <div className="pr-song-name">{s.levelName}</div>
                          <div className="pr-song-meta">
                            <span className="pr-song-badge" style={{ color: badge.color, background: badge.bg }}>
                              {badge.label}
                            </span>
                            <span className="pr-song-meta-item">{s.plays} {s.plays === 1 ? "play" : "plays"}</span>
                            <span className="pr-song-meta-item">{s.minutes} min</span>
                            {s.accuracy != null && (
                              <span className="pr-song-meta-item">{s.accuracy}% accuracy</span>
                            )}
                          </div>
                        </div>
                        <div className="pr-song-right">
                          {s.bestStars > 0 && <StarRow count={s.bestStars} />}
                          <div className="pr-song-when">{fmtDate(s.lastPlayedTs)}</div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {/* ── Recent activity ─────────────────────────────────── */}
            <section className="pr-section">
              <h2 className="pr-h2">Recent activity</h2>
              <div className="pr-recent">
                {summary.lastSessions.map((s) => {
                  const badge = sourceBadge(s.source);
                  const acc = s.totalNotes > 0 ? Math.round((s.correctNotes / s.totalNotes) * 100) : null;
                  return (
                    <div key={s.id} className="pr-recent-row">
                      <div className="pr-recent-time">{fmtDateTime(s.ts)}</div>
                      <div className="pr-recent-body">
                        <span className="pr-song-badge" style={{ color: badge.color, background: badge.bg }}>
                          {badge.label}
                        </span>
                        <span className="pr-recent-name">{s.levelName}</span>
                      </div>
                      <div className="pr-recent-stats">
                        {acc != null && <span>{acc}%</span>}
                        {s.stars != null && s.stars > 0 && <StarRow count={s.stars} />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>

            {/* ── Footer / since ─────────────────────────────────── */}
            <section className="pr-since">
              Learning since {fmtDate(summary.firstPlayedAt)} · Last played {fmtDate(summary.lastPlayedAt)}
            </section>

            {/* ── Parent tools ───────────────────────────────────── */}
            <section className="pr-tools pr-no-print">
              {!confirmClear ? (
                <button className="pr-tool-link" onClick={() => setConfirmClear(true)}>
                  Reset progress data
                </button>
              ) : (
                <div className="pr-confirm">
                  <span>This will clear all local progress data. Are you sure?</span>
                  <button className="pr-confirm-yes" onClick={handleClear}>Yes, clear</button>
                  <button className="pr-confirm-no" onClick={() => setConfirmClear(false)}>Cancel</button>
                </div>
              )}
            </section>
          </>
        )}
      </main>
    </div>
  );
}

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Fredoka+One&family=Nunito:wght@600;700;800;900&display=swap');

.pr-root {
  min-height: 100dvh;
  background: linear-gradient(180deg, #fafbfc 0%, #f1f5f9 100%);
  font-family: 'Nunito', sans-serif;
  color: #0f172a;
}
.pr-root * { box-sizing: border-box; }

.pr-header {
  position: sticky; top: 0; z-index: 50;
  display: flex; align-items: center; gap: 10px;
  padding: 11px 16px;
  background: rgba(255,255,255,0.94);
  backdrop-filter: blur(12px);
  border-bottom: 1px solid #E2E8F0;
}
.pr-back, .pr-print {
  width: 36px; height: 36px; border-radius: 10px;
  border: 2px solid #E2E8F0; background: #fff;
  font-size: 1rem; font-weight: 800; color: #0f172a;
  cursor: pointer; display: flex; align-items: center; justify-content: center;
  transition: all 0.15s;
}
.pr-back:hover, .pr-print:hover { border-color: #94A3B8; transform: translateY(-1px); }
.pr-title-wrap { flex: 1; }
.pr-title {
  font-family: 'Fredoka One', cursive;
  font-size: 1.1rem;
  color: #0f172a;
  line-height: 1.1;
}
.pr-subtitle {
  font-size: 0.74rem;
  font-weight: 700;
  color: #64748B;
  margin-top: 2px;
}

.pr-main {
  max-width: 760px;
  margin: 0 auto;
  padding: 20px 16px 80px;
}

.pr-empty {
  text-align: center;
  padding: 50px 20px;
  background: #fff;
  border-radius: 22px;
  border: 1.5px solid #E2E8F0;
}
.pr-empty-emoji { font-size: 3rem; margin-bottom: 10px; }
.pr-empty-title { font-family: 'Fredoka One', cursive; font-size: 1.3rem; color: #0f172a; }
.pr-empty-sub { color: #64748B; font-weight: 700; font-size: 0.88rem; margin-top: 6px; max-width: 320px; margin-left: auto; margin-right: auto; }
.pr-empty-btn {
  margin-top: 16px;
  padding: 11px 22px; border-radius: 100px;
  background: linear-gradient(135deg,#7C3AED,#6D28D9);
  color: #fff; border: none; cursor: pointer;
  font-family: 'Fredoka One', cursive; font-size: 0.92rem;
  box-shadow: 0 5px 14px rgba(124,58,237,0.3);
}

.pr-stats {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 9px;
  margin-bottom: 22px;
}
.pr-stat {
  background: #fff;
  border: 1.5px solid #E2E8F0;
  border-radius: 16px;
  padding: 12px 8px;
  text-align: center;
}
.pr-stat-emoji { font-size: 1.3rem; line-height: 1; }
.pr-stat-val {
  font-family: 'Fredoka One', cursive;
  font-size: 1.4rem;
  color: #0f172a;
  margin-top: 4px; line-height: 1.05;
}
.pr-stat-lbl {
  font-size: 0.62rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #94A3B8;
  margin-top: 4px;
}

.pr-section { margin-bottom: 22px; }
.pr-h2 {
  font-family: 'Fredoka One', cursive;
  font-size: 0.95rem;
  color: #334155;
  margin: 0 0 9px 4px;
}

.pr-today-card {
  background: #fff;
  border: 1.5px solid #E2E8F0;
  border-radius: 16px;
  padding: 14px 16px;
}
.pr-today-row { display: flex; align-items: flex-end; justify-content: space-between; gap: 10px; margin-bottom: 10px; }
.pr-today-mins { font-family: 'Fredoka One', cursive; font-size: 1.6rem; color: #0f172a; line-height: 1; }
.pr-today-sub { font-size: 0.72rem; font-weight: 700; color: #94A3B8; margin-top: 3px; }
.pr-today-status {
  font-size: 0.78rem;
  font-weight: 800;
  color: #475569;
  text-align: right;
}
.pr-today-bar {
  height: 10px; border-radius: 999px;
  background: #F1F5F9; overflow: hidden;
  border: 1px solid #E2E8F0;
}
.pr-today-fill {
  height: 100%; border-radius: 999px;
  transition: width 0.5s cubic-bezier(.34,1.56,.64,1);
}

.pr-chart {
  background: #fff;
  border: 1.5px solid #E2E8F0;
  border-radius: 16px;
  padding: 14px 10px 10px;
  display: flex; align-items: flex-end; justify-content: space-between;
  gap: 4px;
  height: 160px;
}
.pr-bar-wrap {
  flex: 1; display: flex; flex-direction: column; align-items: center;
  height: 100%; min-width: 0;
}
.pr-bar-value {
  font-size: 0.6rem; font-weight: 800; color: #64748B;
  height: 12px; margin-bottom: 2px;
}
.pr-bar-track {
  flex: 1; width: 100%; max-width: 22px;
  background: #F8FAFC;
  border-radius: 6px;
  display: flex; align-items: flex-end;
  overflow: hidden;
}
.pr-bar-fill { width: 100%; border-radius: 6px; transition: height 0.4s ease-out; min-height: 0; }
.pr-bar-label {
  font-size: 0.58rem;
  font-weight: 800;
  color: #94A3B8;
  margin-top: 5px;
  text-transform: uppercase;
}
.pr-chart-legend {
  display: flex; align-items: center;
  font-size: 0.7rem; font-weight: 700; color: #64748B;
  margin-top: 8px; padding-left: 4px;
}
.pr-legend-dot {
  display: inline-block;
  width: 8px; height: 8px; border-radius: 50%;
  margin-right: 5px;
}

.pr-song-list {
  background: #fff;
  border: 1.5px solid #E2E8F0;
  border-radius: 16px;
  overflow: hidden;
}
.pr-song-row {
  display: flex; align-items: center; gap: 10px;
  padding: 12px 14px;
  border-bottom: 1px solid #F1F5F9;
}
.pr-song-row:last-child { border-bottom: none; }
.pr-song-main { flex: 1; min-width: 0; }
.pr-song-name {
  font-family: 'Fredoka One', cursive;
  font-size: 0.92rem;
  color: #0f172a;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pr-song-meta {
  display: flex; flex-wrap: wrap; gap: 6px 10px;
  margin-top: 3px;
  font-size: 0.7rem; font-weight: 700; color: #64748B;
  align-items: center;
}
.pr-song-badge {
  display: inline-block;
  font-size: 0.6rem;
  font-weight: 900;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  padding: 2px 7px;
  border-radius: 999px;
}
.pr-song-meta-item { white-space: nowrap; }
.pr-song-right { text-align: right; flex-shrink: 0; }
.pr-song-when {
  font-size: 0.66rem;
  font-weight: 700;
  color: #94A3B8;
  margin-top: 3px;
}
.pr-empty-small { color: #94A3B8; font-weight: 700; font-size: 0.84rem; padding: 14px 16px; background: #fff; border: 1.5px solid #E2E8F0; border-radius: 16px; }

.pr-recent {
  background: #fff;
  border: 1.5px solid #E2E8F0;
  border-radius: 16px;
  overflow: hidden;
}
.pr-recent-row {
  display: flex; align-items: center; gap: 10px;
  padding: 10px 14px;
  border-bottom: 1px solid #F1F5F9;
  font-size: 0.78rem;
}
.pr-recent-row:last-child { border-bottom: none; }
.pr-recent-time {
  font-size: 0.66rem; font-weight: 700; color: #94A3B8;
  width: 86px; flex-shrink: 0;
}
.pr-recent-body {
  flex: 1; min-width: 0;
  display: flex; align-items: center; gap: 7px;
}
.pr-recent-name {
  font-weight: 800; color: #334155;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}
.pr-recent-stats {
  display: flex; align-items: center; gap: 6px;
  font-size: 0.72rem; font-weight: 800; color: #475569;
  flex-shrink: 0;
}

.pr-since {
  text-align: center;
  font-size: 0.72rem; font-weight: 700;
  color: #94A3B8;
  margin-top: 6px; margin-bottom: 16px;
}

.pr-tools { text-align: center; margin-top: 22px; }
.pr-tool-link {
  background: none; border: none;
  font-size: 0.72rem; font-weight: 700;
  color: #94A3B8;
  text-decoration: underline;
  text-decoration-style: dashed;
  text-underline-offset: 3px;
  cursor: pointer;
}
.pr-tool-link:hover { color: #475569; }
.pr-confirm {
  display: inline-flex; align-items: center; gap: 9px;
  background: #FEF2F2; border: 1.5px solid #FECACA;
  border-radius: 100px; padding: 8px 14px;
  font-size: 0.74rem; font-weight: 700; color: #991B1B;
  flex-wrap: wrap; justify-content: center;
}
.pr-confirm-yes {
  background: #DC2626; color: #fff;
  border: none; border-radius: 100px;
  padding: 5px 12px; font-weight: 800; font-size: 0.7rem;
  cursor: pointer;
}
.pr-confirm-no {
  background: #fff; color: #475569;
  border: 1.5px solid #CBD5E1; border-radius: 100px;
  padding: 5px 12px; font-weight: 800; font-size: 0.7rem;
  cursor: pointer;
}

@media (min-width: 600px) {
  .pr-stats { grid-template-columns: repeat(6, 1fr); }
  .pr-chart { height: 180px; }
  .pr-bar-track { max-width: 28px; }
}

@media print {
  .pr-no-print, .pr-back, .pr-print { display: none !important; }
  .pr-root { background: #fff; }
  .pr-header { position: static; border-bottom: 2px solid #0f172a; }
  .pr-section { break-inside: avoid; }
}
`;